import Text from "@/components/ui/text";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { isEmpty } from "lodash";
import { DreamerBarChartDataType } from "@/components/DreamerCharts/types";
import { formatNumber } from "@/utils/numbers";
import LegendWithMore from "@/components/DreamerCharts/LegendWithMore";

interface Props {
  height: number | string;
  chartData: DreamerBarChartDataType[];
  isPercentage?: boolean;
  chartClassName?: string;
  legendClassName?: string;
  barColor?: string;
  showLegend?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  tooltipYpos?: number;
  compact?: boolean;
}

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const effectiveHeight = Math.max(height, 2);
  const adjustedY = y + (height - effectiveHeight);
  const cornerRadius = 3;
  return (
    <path
      d={`
        M ${x},${adjustedY + effectiveHeight}
        L ${x},${adjustedY + cornerRadius}
        Q ${x},${adjustedY} ${x + cornerRadius},${adjustedY}
        L ${x + width - cornerRadius},${adjustedY}
        Q ${x + width},${adjustedY} ${x + width},${adjustedY + cornerRadius}
        L ${x + width},${adjustedY + effectiveHeight}
        Z
      `}
      fill={fill}
    />
  );
};

// Approximate glyph width in px at fontSize 10, used to fit labels to their
// axis slot without measuring rendered text.
const X_TICK_CHAR_WIDTH_PX = 6;
const X_TICK_MIN_CHARS = 4;

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  index?: number;
  // Injected by recharts' CartesianAxis (internal, verified on 2.12 and
  // guarded by the integration test): full axis width and tick count.
  width?: number;
  visibleTicksCount?: number;
  visibleIndices?: Set<number>;
  unknownLabel: string;
}

export const CustomXAxisTick: React.FC<CustomXAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  index,
  width,
  visibleTicksCount,
  visibleIndices,
  unknownLabel
}) => {
  if (
    visibleIndices &&
    index !== undefined &&
    !visibleIndices.has(index)
  ) {
    return <g />;
  }
  const rawValue = payload?.value;
  const value = rawValue != null && rawValue !== "" ? rawValue : unknownLabel;
  // Truncate to the label's share of the axis so adjacent ticks never
  // overlap. The full tick count stays the divisor even when visibleIndices
  // hides ticks: the survivors sit centre-anchored on the outermost bands,
  // so a label wider than ~one band overflows the SVG rather than using the
  // freed axis space.
  const slotWidth =
    width && visibleTicksCount ? width / visibleTicksCount : null;
  const maxChars = slotWidth
    ? Math.max(X_TICK_MIN_CHARS, Math.floor(slotWidth / X_TICK_CHAR_WIDTH_PX))
    : null;
  // Code points, not UTF-16 units — slicing mid-surrogate corrupts emoji.
  const valueChars = Array.from(value);
  const isTruncated = maxChars !== null && valueChars.length > maxChars;
  const text = isTruncated
    ? `${valueChars.slice(0, maxChars - 1).join("")}…`
    : value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy="0.71em"
        textAnchor="middle"
        fill="var(--secondary)"
        fontSize={10}
      >
        {isTruncated ? <title>{value}</title> : null}
        {text}
      </text>
    </g>
  );
};

const SimpleTooltip = ({
  active,
  payload,
  isPercentage,
  unknownLabel
}: TooltipProps<ValueType, NameType> & { isPercentage?: boolean; unknownLabel: string }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DreamerBarChartDataType;
    const { value, modifier } = formatNumber(data.value, 2);
    return (
      <div className="bg-surface-elevation3 border border-border-border1 rounded-lg p-2 shadow-lg">
        <Text variant="smBody" className="text-primary">
          {data.label || unknownLabel}: {value}
          {modifier}
          {isPercentage ? "%" : ""}
        </Text>
      </div>
    );
  }
  return null;
};

const DreamerBarChart: React.FC<Props> = ({
  height,
  chartData,
  isPercentage = true,
  chartClassName,
  legendClassName,
  barColor = "url(#dreamer-gradient)",
  showLegend = true,
  xAxisLabel,
  yAxisLabel,
  tooltipYpos = 10,
  compact = false
}) => {
  const { t } = useTranslation();
  if (isEmpty(chartData)) return null;

  const unknownLabel = t("unknown");
  const barSize =
    chartData?.length === 1 ? 80 : chartData?.length <= 6 ? 32 : 8;

  const visibleXIndices =
    compact && chartData.length > 6
      ? new Set([0, chartData.length - 1])
      : undefined;

  const Y_AXIS_LABEL_MAX_CHARS = 24;
  const truncatedYAxisLabel =
    yAxisLabel && yAxisLabel.length > Y_AXIS_LABEL_MAX_CHARS
      ? `${yAxisLabel.slice(0, Y_AXIS_LABEL_MAX_CHARS - 1)}…`
      : yAxisLabel;

  // LegendWithMore bullets need a real CSS color. The default barColor is a
  // gradient URL (`url(#dreamer-gradient)`) which won't render in the bullet —
  // fall back to the CTA color the gradient is based on.
  const legendBulletColor = barColor.startsWith("url(") ? "var(--cta)" : barColor;
  const containerHeight = typeof height === "number" ? `${height}px` : height;
  return (
    <div
      className="flex flex-col w-full"
      style={{ height: containerHeight }}
    >
      <div className="flex-1 min-h-0 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart
          data={chartData}
          margin={{
            left: 10,
            right: 10,
            top: 5,
            bottom: xAxisLabel ? 10 : 0
          }}
          className={chartClassName}
        >
          <defs>
            <linearGradient id="dreamer-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="30%" stopColor="var(--cta)" stopOpacity={0.8} />
              <stop offset="95%" stopColor="var(--cta)" stopOpacity={0.5} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="label"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={
              <CustomXAxisTick
                visibleIndices={visibleXIndices}
                unknownLabel={unknownLabel}
              />
            }
            label={
              xAxisLabel
                ? {
                    value: xAxisLabel,
                    position: "insideBottom",
                    offset: -4,
                    fill: "var(--tertiary)",
                    fontSize: 12
                  }
                : undefined
            }
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
            tickFormatter={(tick) => {
              const { value, modifier } = formatNumber(Number(tick), 2);
              return `${value}${modifier}${isPercentage ? "%" : ""}`;
            }}
            width={yAxisLabel ? 60 : 15}
            label={
              truncatedYAxisLabel
                ? {
                    value: truncatedYAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    fill: "var(--tertiary)",
                    fontSize: 12,
                    style: { textAnchor: "middle" }
                  }
                : undefined
            }
          />

          <Bar
            dataKey="value"
            shape={(props: any) => {
              const { x, y, width, height } = props;
              return (
                <CustomBar
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill={barColor}
                />
              );
            }}
            barSize={barSize}
          />

          <RechartsTooltip
            content={<SimpleTooltip isPercentage={isPercentage} unknownLabel={unknownLabel} />}
            cursor={{ fill: "transparent" }}
            position={{ y: tooltipYpos }}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
      </div>

      {showLegend && (
        <LegendWithMore
          items={chartData.map((item, index) => ({
            key: `${item.label ?? unknownLabel}-${index}`,
            label: item.label || unknownLabel,
            color: legendBulletColor
          }))}
          className={legendClassName}
        />
      )}
    </div>
  );
};

export default DreamerBarChart;
